import { useEffect, useState } from 'react';

import { Box, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';

import { Send } from 'domains/campaigns/types';
import Autocomplete from 'domains/core/components/Autocomplete';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import NestedFilterBuilder from 'domains/segments/components/NestedFilterBuilder';
import { NestedSegmentDefinitionInput, Segment, SegmentDefinition } from 'domains/segments/types';
import useSegment from 'hooks/queries/useSegment';

type SegmentCriteriaMode = 'saved' | 'custom';

// A stored definition that carries a segmentId is a snapshot of a saved audience (picked via the
// dropdown). One without an id was built inline with the custom-rules builder. We use this to default
// the mode sensibly on edit.
export const getInitialMode = (definition?: SegmentDefinition | null): SegmentCriteriaMode =>
    definition && !definition.segmentId ? 'custom' : 'saved';

export type SegmentCriteriaModeBuilderProps = {
    initialDefinition?: SegmentDefinition | null;
    segments?: Segment[];
    isSegmentsLoading: boolean;
    campaignSends?: Send[];
    /** Label for the saved-audience picker. Defaults to "Audience". */
    audienceLabel?: string;
    /** aria-label for the saved/custom mode RadioGroup. Defaults to "Exit criteria mode". */
    modeAriaLabel?: string;
    /** Prefix for the mode radios' data-testid (`{prefix}-mode-saved` / `{prefix}-mode-custom`). Defaults to "exit". */
    modeTestIdPrefix?: string;
    /** Locks the whole builder (pointer-events + dimmed) without unmounting it. Defaults to false. */
    disabled?: boolean;
    onChange: (definition: SegmentDefinition | null, isValid: boolean) => void;
    /**
     * Reports the saved-audience fetch's loading/error state on every change, so a consumer that
     * shows a derived value (e.g. a matching-contact count) can avoid displaying stale data or a
     * silent failure while a newly-picked audience's definition is still loading. Optional — the
     * split criteria step has no such derived value and doesn't need this signal.
     */
    onSavedAudienceFetchStatusChange?: (isFetching: boolean, isError: boolean) => void;
};

/**
 * Dialog-agnostic dual-mode segment criteria builder: pick a saved audience (seeds the definition)
 * or build custom rules inline via NestedFilterBuilder. Shared by ExitCriteriaDialog (wrapped in a
 * modal) and the conditional-split criteria step (rendered inline in a form page) so the mode-toggle
 * and rule-building logic isn't duplicated between the two.
 */
const SegmentCriteriaModeBuilder = ({
    initialDefinition,
    segments,
    isSegmentsLoading,
    campaignSends,
    audienceLabel = 'Audience',
    modeAriaLabel = 'Exit criteria mode',
    modeTestIdPrefix = 'exit',
    disabled = false,
    onChange,
    onSavedAudienceFetchStatusChange,
}: SegmentCriteriaModeBuilderProps) => {
    const [mode, setMode] = useState<SegmentCriteriaMode>(getInitialMode(initialDefinition));
    const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(initialDefinition?.segmentId ?? null);
    // Snapshot of the definition to seed the custom-rules builder on first render only (so subsequent
    // edits aren't fought by a re-prefill). Captured when custom mode is the initial mode.
    const [customSeed] = useState<SegmentDefinition | null>(() =>
        getInitialMode(initialDefinition) === 'custom' ? initialDefinition ?? null : null
    );

    const { isFetching: isSegmentFetching, isError: isSegmentError } = useSegment(selectedSegmentId, {
        enabled: Boolean(selectedSegmentId),
        onSuccess: (segment) => {
            onChange({ ...segment.segmentDefinition, segmentId: selectedSegmentId ?? undefined }, true);
        },
    });

    useEffect(() => {
        onSavedAudienceFetchStatusChange?.(isSegmentFetching, isSegmentError);
        // onSavedAudienceFetchStatusChange identity is owned by the consumer; depend only on the
        // status values themselves.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSegmentFetching, isSegmentError]);

    const segmentOptions: AutocompleteOption<number>[] = (segments ?? []).map((segment) => ({
        label: segment.name,
        value: segment.id,
    }));
    const selectedOption = segmentOptions.find((option) => option.value === selectedSegmentId) ?? null;

    const handleModeChange = (nextMode: SegmentCriteriaMode) => {
        if (nextMode === mode) return;
        setMode(nextMode);
        // Switching modes discards the previously defined criteria so the two modes never overlap.
        setSelectedSegmentId(null);
        onChange(null, false);
    };

    const handleSegmentChange = (option: AutocompleteOption<number> | null) => {
        const segmentId = option?.value ?? null;
        setSelectedSegmentId(segmentId);
        // The definition is reported in useSegment's onSuccess once it has been fetched.
        if (!segmentId) onChange(null, false);
    };

    const handleCustomChange = (definition: NestedSegmentDefinitionInput, isValid: boolean) => {
        onChange(isValid ? (definition as SegmentDefinition) : null, isValid);
    };

    return (
        <Stack
            spacing={2}
            data-testid="segment-criteria-mode-builder"
            sx={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
        >
            <RadioGroup
                row
                aria-label={modeAriaLabel}
                value={mode}
                onChange={(e) => handleModeChange(e.target.value as SegmentCriteriaMode)}
            >
                <FormControlLabel
                    data-testid={`${modeTestIdPrefix}-mode-saved`}
                    value="saved"
                    control={<Radio size="small" color="secondary" />}
                    label={<Typography variant="body2">Use a saved audience</Typography>}
                />
                <FormControlLabel
                    data-testid={`${modeTestIdPrefix}-mode-custom`}
                    value="custom"
                    control={<Radio size="small" color="secondary" />}
                    label={<Typography variant="body2">Create custom rules</Typography>}
                />
            </RadioGroup>
            {mode === 'saved' ? (
                <Autocomplete
                    getOptionLabel={(option) => option.label ?? ''}
                    label={audienceLabel}
                    loading={isSegmentsLoading}
                    onChange={(_e: any, option: AutocompleteOption<number> | null) => handleSegmentChange(option)}
                    options={segmentOptions}
                    placeholder="Select an audience"
                    type="string"
                    value={selectedOption}
                />
            ) : (
                // Bottom padding so the builder's Add filter / Add filter group buttons aren't flush
                // against whatever renders below (dialog actions, or the step's continue button).
                <Box sx={{ pb: 2 }}>
                    <NestedFilterBuilder
                        initialDefinition={customSeed}
                        onChange={handleCustomChange}
                        additionalSends={campaignSends}
                    />
                </Box>
            )}
        </Stack>
    );
};

export default SegmentCriteriaModeBuilder;
